(function () {
  'use strict';

  var config = window.HSOCurrencyConfig || {};
  var baseCurrency = String(config.baseCurrency || 'CLP').toUpperCase();
  var supportedCurrencies = Array.isArray(config.currencies) && config.currencies.length
    ? config.currencies.map(function (currency) { return String(currency).toUpperCase(); })
    : ['CLP', 'USD', 'ARS', 'BRL', 'PEN', 'MXN', 'EUR'];

  var storageKeys = {
    selectedCurrency: 'hso_selected_currency_v1',
    rates: 'hso_currency_rates_v1'
  };

  var state = {
    selectedCurrency: baseCurrency,
    rates: null,
    observer: null,
    isUpdating: false,
    refreshTimer: null
  };

  /*
   * Selectores nativos y habituales del tema Furstore/Vinova.
   * Se mantienen los selectores de la primera versión y se agregan
   * los utilizados en tarjetas de colección, carruseles y módulos home.
   */
  var moneySelectors = [
    '[data-hso-money-cents]',
    '[data-hso-money-text]',
    '[data-hso-original-html]',
    '.money',
    '.price-item',
    '.product-price',
    '.product-price__price',
    '.product-price__price--regular',
    '.product-price__price--sale',
    '.product-price__regular',
    '.product-price__sale',
    '.product-price__compare',
    '.product-price__old',
    '.product-price__new',
    '.product-item__price',
    '.product-card__price',
    '.item-product__price',
    '.product__price',
    '.product-single__price',
    '.current-price',
    '.sale-price',
    '.regular-price',
    '.compare-price',
    '.old-price',
    '.new-price',
    '.special-price',
    '.price-old',
    '.price-new',
    '.price_regular',
    '.price_sale',
    '.price_compare',
    '.price-box',
    '.price-box__regular',
    '.price-box__sale',
    '.price-box__old',
    '.price-box__new',
    '.price',
    '.f_pr',
    '.product-subtotal',
    '.cart__subtotal',
    '.cart__savings-amount'
  ].join(',');

  /*
   * Contenedores que permiten reconocer precios aunque el tema use
   * una clase de precio distinta en una sección concreta.
   */
  var productContextSelectors = [
    '[data-product-id]',
    '[data-product-handle]',
    '[data-product]',
    '[data-product-json]',
    '.item-product',
    '.product-item',
    '.product-card',
    '.product-miniature',
    '.product-grid-item',
    '.grid-product',
    '.nov-product-item',
    '.product-box',
    '.product-block',
    '.product-content',
    '.product-info',
    '.product__item',
    '.product-list-item',
    '.product-related',
    '.popup__cart-product',
    '.quick-view',
    '.quickview',
    '.quickview-product',
    '.search-results',
    '.wishlist-item',
    '.cart_item',
    '.cart-product'
  ].join(',');

  var ignoredSelectors = [
    '[data-hso-currency-switcher]',
    '.hso-currency-cart-notice',
    '.hso-currency-status',
    'script',
    'style',
    'noscript',
    'template',
    'select',
    'option',
    'textarea',
    '.collection-filters',
    '.collection-filter',
    '.facets',
    '.facet-filters',
    '.filter-group',
    '.price-range',
    '[data-filter]'
  ].join(',');

  var moneyTokenExpression = /(?:CLP\s*)?\$\s*-?\d(?:[\d.\s]*\d)?(?:,\d+)?|-?\d(?:[\d.\s]*\d)?(?:,\d+)?\s*CLP/gi;

  function safeStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* El convertidor continúa funcionando sin persistencia. */
    }
  }

  function normalizeSelectedCurrency(currency) {
    currency = String(currency || '').toUpperCase();
    return supportedCurrencies.indexOf(currency) !== -1 ? currency : baseCurrency;
  }

  function readSelectedCurrency() {
    return normalizeSelectedCurrency(safeStorageGet(storageKeys.selectedCurrency));
  }

  function writeSelectedCurrency(currency) {
    safeStorageSet(storageKeys.selectedCurrency, currency);
  }

  function getCacheLifetimeMilliseconds() {
    var hours = Number(config.cacheHours || 24);
    return Math.max(hours, 1) * 60 * 60 * 1000;
  }

  function getCachedRates() {
    var raw = safeStorageGet(storageKeys.rates);

    if (!raw) {
      return null;
    }

    try {
      var cached = JSON.parse(raw);
      var isCurrentBase = cached && cached.baseCurrency === baseCurrency;
      var isFresh = cached && Number(cached.savedAt) + getCacheLifetimeMilliseconds() > Date.now();
      var hasRates = cached && cached.rates && typeof cached.rates === 'object';

      return isCurrentBase && isFresh && hasRates ? cached.rates : null;
    } catch (error) {
      return null;
    }
  }

  function saveRates(rates) {
    safeStorageSet(storageKeys.rates, JSON.stringify({
      baseCurrency: baseCurrency,
      savedAt: Date.now(),
      rates: rates
    }));
  }

  function loadRates() {
    if (state.rates) {
      return Promise.resolve(state.rates);
    }

    var cachedRates = getCachedRates();

    if (cachedRates) {
      state.rates = cachedRates;
      return Promise.resolve(state.rates);
    }

    var apiUrl = config.apiUrl || ('https://open.er-api.com/v6/latest/' + encodeURIComponent(baseCurrency));

    return fetch(apiUrl, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Currency API HTTP ' + response.status);
        }

        return response.json();
      })
      .then(function (payload) {
        if (!payload || payload.result !== 'success' || !payload.rates) {
          throw new Error('Currency API response invalid');
        }

        state.rates = payload.rates;
        saveRates(payload.rates);
        hideStatusMessage();
        return state.rates;
      });
  }

  function decimalsFor(currency) {
    if (config.decimals && Object.prototype.hasOwnProperty.call(config.decimals, currency)) {
      return Number(config.decimals[currency]);
    }

    return currency === 'CLP' || currency === 'ARS' ? 0 : 2;
  }

  function formatConvertedAmount(amount, currency) {
    var decimals = decimalsFor(currency);
    var locale = config.locale || document.documentElement.lang || 'es-CL';
    var formatted;

    try {
      formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(amount);
    } catch (error) {
      formatted = Number(amount).toFixed(decimals);
    }

    return currency + ' ' + formatted;
  }

  function parseShopifyMoneyText(value) {
    if (typeof value !== 'string') {
      return null;
    }

    var cleaned = value
      .replace(/\u00a0/g, ' ')
      .replace(/CLP/gi, '')
      .replace(/\$/g, '')
      .trim();

    var match = cleaned.match(/-?\d[\d.\s]*(?:,\d+)?/);

    if (!match) {
      return null;
    }

    var numeric = match[0].replace(/\s/g, '');

    if (numeric.indexOf(',') !== -1) {
      numeric = numeric.replace(/\./g, '').replace(',', '.');
    } else {
      numeric = numeric.replace(/\./g, '');
    }

    var parsed = Number(numeric);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function containsMoneyToken(value) {
    if (typeof value !== 'string' || !/[\d]/.test(value)) {
      return false;
    }

    moneyTokenExpression.lastIndex = 0;
    return moneyTokenExpression.test(value);
  }

  function convertBaseAmount(amount, targetCurrency) {
    if (targetCurrency === baseCurrency) {
      return amount;
    }

    if (!state.rates || typeof state.rates[targetCurrency] !== 'number') {
      return null;
    }

    return amount * state.rates[targetCurrency];
  }

  function replaceMoneyTokens(text, targetCurrency) {
    var changed = false;

    moneyTokenExpression.lastIndex = 0;

    var result = text.replace(moneyTokenExpression, function (token) {
      var amount = parseShopifyMoneyText(token);

      if (amount === null) {
        return token;
      }

      var converted = convertBaseAmount(amount, targetCurrency);

      if (converted === null) {
        return token;
      }

      changed = true;
      return formatConvertedAmount(converted, targetCurrency);
    });

    return {
      changed: changed,
      value: result
    };
  }

  function hasNestedMoneyElement(element) {
    var nested = element.querySelector(moneySelectors);
    return Boolean(nested && nested !== element);
  }

  function rememberOriginalElement(element) {
    if (!element.hasAttribute('data-hso-original-html')) {
      element.setAttribute('data-hso-original-html', element.innerHTML);
    }

    if (!element.hasAttribute('data-hso-original-title')) {
      element.setAttribute('data-hso-original-title', element.getAttribute('title') || '');
    }
  }

  function restoreElement(element) {
    if (!element.hasAttribute('data-hso-original-html')) {
      return;
    }

    element.innerHTML = element.getAttribute('data-hso-original-html');
    element.classList.remove('hso-currency-converted');

    var originalTitle = element.getAttribute('data-hso-original-title');

    if (originalTitle) {
      element.setAttribute('title', originalTitle);
    } else {
      element.removeAttribute('title');
    }
  }

  function convertExplicitElement(element, targetCurrency) {
    var cents = Number(element.getAttribute('data-hso-money-cents'));

    if (!Number.isFinite(cents)) {
      return;
    }

    rememberOriginalElement(element);

    if (targetCurrency === baseCurrency) {
      restoreElement(element);
      return;
    }

    var baseAmount = cents / 100;
    var converted = convertBaseAmount(baseAmount, targetCurrency);

    if (converted === null) {
      return;
    }

    element.textContent = formatConvertedAmount(converted, targetCurrency);
    element.classList.add('hso-currency-converted');
    element.setAttribute(
      'title',
      (config.labels && config.labels.original ? config.labels.original : 'Precio original') +
      ': ' + baseCurrency + ' ' + baseAmount
    );
  }

  function convertGenericElement(element, targetCurrency) {
    if (hasNestedMoneyElement(element)) {
      return;
    }

    rememberOriginalElement(element);

    if (targetCurrency === baseCurrency) {
      restoreElement(element);
      return;
    }

    var originalHtml = element.getAttribute('data-hso-original-html');
    var originalText = element.textContent.trim();
    var temporary = document.createElement('div');
    temporary.innerHTML = originalHtml;

    var walker = document.createTreeWalker(temporary, NodeFilter.SHOW_TEXT, null);
    var node;
    var convertedAny = false;

    while ((node = walker.nextNode())) {
      var text = node.nodeValue;

      if (!containsMoneyToken(text)) {
        continue;
      }

      var transformed = replaceMoneyTokens(text, targetCurrency);

      if (transformed.changed) {
        node.nodeValue = transformed.value;
        convertedAny = true;
      }
    }

    if (convertedAny) {
      element.innerHTML = temporary.innerHTML;
      element.classList.add('hso-currency-converted');
      element.setAttribute(
        'title',
        (config.labels && config.labels.original ? config.labels.original : 'Precio original') +
        ': ' + originalText
      );
    }
  }

  function isIgnoredElement(element) {
    return Boolean(element && element.closest && element.closest(ignoredSelectors));
  }

  function isInsideProductContext(element) {
    if (!element || !element.closest) {
      return false;
    }

    if (element.closest(productContextSelectors)) {
      return true;
    }

    if (element.closest('form[action*="/cart/add"], form[action$="/cart/add"]')) {
      return true;
    }

    /*
     * Respaldo para tarjetas con nombres de clase propios del editor:
     * buscamos un enlace de producto dentro de los primeros ancestros,
     * evitando llegar a contenedores generales de página.
     */
    var ancestor = element;
    var levels = 0;

    while (ancestor && ancestor !== document.body && levels < 8) {
      if (
        ancestor.matches &&
        !ancestor.matches('main, section, header, footer') &&
        ancestor.querySelector &&
        ancestor.querySelector('a[href*="/products/"]')
      ) {
        return true;
      }

      ancestor = ancestor.parentElement;
      levels += 1;
    }

    return false;
  }

  function getFallbackMoneyElements(root) {
    root = root || document;

    var walkerRoot = root.nodeType === Node.DOCUMENT_NODE ? root.body : root;

    if (!walkerRoot || !document.createTreeWalker) {
      return [];
    }

    var walker = document.createTreeWalker(walkerRoot, NodeFilter.SHOW_TEXT, null);
    var node;
    var elements = [];

    while ((node = walker.nextNode())) {
      if (!containsMoneyToken(node.nodeValue)) {
        continue;
      }

      var parent = node.parentElement;

      if (!parent || isIgnoredElement(parent)) {
        continue;
      }

      /* Los selectores conocidos se procesan por la vía principal. */
      if (parent.closest(moneySelectors)) {
        continue;
      }

      if (!isInsideProductContext(parent)) {
        continue;
      }

      elements.push(parent);
    }

    return elements;
  }

  function getMoneyElements(root) {
    root = root || document;
    var elements = [];

    if (root.nodeType === Node.ELEMENT_NODE && root.matches && root.matches(moneySelectors)) {
      elements.push(root);
    }

    if (root.querySelectorAll) {
      Array.prototype.push.apply(elements, root.querySelectorAll(moneySelectors));
    }

    Array.prototype.push.apply(elements, getFallbackMoneyElements(root));

    return elements.filter(function (element, index, array) {
      return (
        array.indexOf(element) === index &&
        !isIgnoredElement(element)
      );
    });
  }

  function refreshPrices(root) {
    if (state.isUpdating) {
      return;
    }

    state.isUpdating = true;

    try {
      getMoneyElements(root).forEach(function (element) {
        if (element.hasAttribute('data-hso-money-cents')) {
          convertExplicitElement(element, state.selectedCurrency);
        } else {
          convertGenericElement(element, state.selectedCurrency);
        }
      });

      updateCartNotice();
    } finally {
      window.setTimeout(function () {
        state.isUpdating = false;
      }, 0);
    }
  }

  function resetTrackedElements(root) {
    root = root || document;
    var selector = '[data-hso-original-html]';
    var elements = [];

    if (root.nodeType === Node.ELEMENT_NODE && root.matches && root.matches(selector)) {
      elements.push(root);
    }

    if (root.querySelectorAll) {
      Array.prototype.push.apply(elements, root.querySelectorAll(selector));
    }

    elements.forEach(function (element) {
      restoreElement(element);
      element.removeAttribute('data-hso-original-html');
      element.removeAttribute('data-hso-original-title');
    });
  }

  function syncSelectors() {
    document.querySelectorAll('[data-hso-currency-select]').forEach(function (select) {
      select.value = state.selectedCurrency;
      select.setAttribute('aria-label', 'Moneda de visualización: ' + state.selectedCurrency);
    });
  }

  function updateCartNotice() {
    document.querySelectorAll('[data-hso-currency-notice]').forEach(function (notice) {
      var selectedLabel = notice.querySelector('[data-hso-selected-currency-label]');
      var isForeignCurrency = state.selectedCurrency !== baseCurrency;

      if (selectedLabel) {
        selectedLabel.textContent = state.selectedCurrency;
      }

      notice.classList.toggle('is-visible', isForeignCurrency);
      notice.setAttribute('aria-hidden', isForeignCurrency ? 'false' : 'true');
    });
  }

  function showStatusMessage() {
    document.querySelectorAll('[data-hso-currency-status]').forEach(function (status) {
      status.textContent = config.labels && config.labels.unavailable
        ? config.labels.unavailable
        : 'Conversión temporalmente no disponible';
      status.classList.add('is-visible');
    });
  }

  function hideStatusMessage() {
    document.querySelectorAll('[data-hso-currency-status]').forEach(function (status) {
      status.classList.remove('is-visible');
      status.textContent = '';
    });
  }

  function applySelectedCurrency(currency) {
    state.selectedCurrency = normalizeSelectedCurrency(currency);
    writeSelectedCurrency(state.selectedCurrency);
    syncSelectors();

    if (state.selectedCurrency === baseCurrency) {
      refreshPrices(document);
      hideStatusMessage();
      return Promise.resolve();
    }

    return loadRates()
      .then(function () {
        refreshPrices(document);
      })
      .catch(function (error) {
        console.error('[HSO Currency Converter]', error);
        state.selectedCurrency = baseCurrency;
        writeSelectedCurrency(baseCurrency);
        syncSelectors();
        refreshPrices(document);
        showStatusMessage();
      });
  }

  function bindSelectors() {
    document.addEventListener('change', function (event) {
      var select = event.target.closest('[data-hso-currency-select]');

      if (!select) {
        return;
      }

      applySelectedCurrency(select.value);
    });
  }

  function scheduleRefresh(root) {
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = window.setTimeout(function () {
      resetTrackedElements(root || document);
      refreshPrices(root || document);
    }, 80);
  }

  function observeDynamicChanges() {
    if (!window.MutationObserver || !document.body) {
      return;
    }

    state.observer = new MutationObserver(function (mutations) {
      if (state.isUpdating) {
        return;
      }

      var relevantRoot = null;

      mutations.some(function (mutation) {
        if (mutation.type === 'childList' && (mutation.addedNodes.length || mutation.removedNodes.length)) {
          relevantRoot = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : document;
          return true;
        }

        if (mutation.type === 'characterData') {
          relevantRoot = mutation.target.parentElement || document;
          return true;
        }

        return false;
      });

      if (relevantRoot) {
        scheduleRefresh(relevantRoot);
      }
    });

    state.observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function insertSelectorsWhenMissing() {
    if (document.querySelector('[data-hso-currency-switcher]')) {
      return;
    }

    var template = document.getElementById('HSOCurrencySelectorTemplate');

    if (!template || !template.content) {
      return;
    }

    var desktopTarget = document.querySelector('.nov-header-desktop .header-group-item.justify-content-end');
    var mobileTarget = document.querySelector('.header-mobile .mobile_cart');
    var stickyTarget = document.querySelector('#header-sticky .header-group-item');

    [desktopTarget, mobileTarget, stickyTarget].forEach(function (target) {
      if (target) {
        target.insertBefore(template.content.cloneNode(true), target.firstChild);
      }
    });
  }

  function init() {
    insertSelectorsWhenMissing();
    state.selectedCurrency = readSelectedCurrency();
    bindSelectors();
    syncSelectors();
    updateCartNotice();
    observeDynamicChanges();
    applySelectedCurrency(state.selectedCurrency);

    document.addEventListener('shopify:section:load', function (event) {
      insertSelectorsWhenMissing();
      syncSelectors();
      scheduleRefresh(event.target || document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
